import { useEffect, type ReactNode } from 'react';
import { setAuth, setAuthLoading } from '~/lib/stores/auth';
import { isAxiosError } from 'axios';
import { apiRefreshToken } from '~/lib/api/auth';
import { isNetworkError } from '~/lib/network-state';
import { initializePreferencesFromServer } from '~/lib/stores/settings';
import { migrateProfileFromLocalStorage } from '~/lib/stores/profile';
import { loadProviderList } from '~/lib/provider-list';

interface AuthProviderProps {
  children: ReactNode;
}

/** Waits between session-restore attempts while the server can't be reached. */
export const RESTORE_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000];

/**
 * Only an answer from the server that there is no session means signed out. A
 * page loaded while studio restarts or deploys got a proxy error / 502 on
 * /api/auth/refresh and was shown signed out with a perfectly valid session
 * cookie (Render logs 502s around every studio boot). Retry those with backoff.
 */
export function isTransientRefreshError(err: unknown): boolean {
  if (isNetworkError(err)) {
    return true;
  }

  const status = isAxiosError(err) ? err.response?.status : undefined;

  return typeof status === 'number' && status >= 500;
}

export async function refreshWithRetry(
  isCancelled: () => boolean,
  refresh: typeof apiRefreshToken = apiRefreshToken,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): ReturnType<typeof apiRefreshToken> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await refresh();
    } catch (err) {
      if (attempt >= RESTORE_RETRY_DELAYS_MS.length || !isTransientRefreshError(err) || isCancelled()) {
        throw err;
      }

      await sleep(RESTORE_RETRY_DELAYS_MS[attempt]);
    }
  }
}

export default function AuthProvider({ children }: AuthProviderProps) {
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        const { accessToken, user } = await refreshWithRetry(() => cancelled);

        if (!cancelled) {
          setAuth(accessToken, user);

          // Load provider/model data and user preferences from server
          await Promise.all([loadProviderList(), initializePreferencesFromServer()]);

          // Migrate profile from localStorage if present
          migrateProfileFromLocalStorage().catch(console.error);
        }
      } catch {
        // No valid session — still load public provider list so the chat UI works
        if (!cancelled) {
          loadProviderList().catch(console.error);
        }
      } finally {
        if (!cancelled) {
          setAuthLoading(false);
        }
      }
    }

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  return <>{children}</>;
}
